import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { useAtomValue } from "jotai";
import { useCallback } from "react";
import { Shape, StickyNote, TextBlock } from "shared/datamodel/schemas";
import { documentIdAtom, submenuDataAtom, syncServiceAtom, undoRedoStackAtom, utilsAtom } from "state-atoms";
import { useCanvasElementById } from "subscriptions";
import {
  getFieldText,
  isElementCentered,
  getFieldSize,
  findNextAvailableFieldPosition,
  getDerivedTextBlockPositions,
} from "../dynamic-data-utils";
import { useDynamicElementActions } from "./use-dynamic-element-actions";
import tracking from "frontend/tracking";

export const useFieldDisplay = (elementId: string, fieldId: string, fieldValue?: string) => {
  const submenuData = useAtomValue(submenuDataAtom);
  const documentId = useAtomValue(documentIdAtom).documentId;
  const canvasUtils = useAtomValue(utilsAtom(documentId));

  const { addFieldToElement } = useDynamicElementActions();

  const [, integrationId, itemId, columnId] = fieldId.split(":");
  const fieldText = getFieldText(elementId, undefined, { integrationId, columnId, itemId });

  const syncService = useAtomValue(syncServiceAtom);
  const reflect = syncService?.getReplicache();
  const undoRedoStack = useAtomValue(undoRedoStackAtom);

  const parentElement = useCanvasElementById(reflect, elementId, null, [elementId]) as Shape | StickyNote | null;
  const memoizedParentElement = useDeepEqualMemo(parentElement);
  const textBlockId = memoizedParentElement?.dataLayer?.elementMapping?.[fieldId];

  const textBlockElement = useCanvasElementById(reflect, textBlockId, null, [textBlockId]) as TextBlock | null;
  const memoizedFieldTextBlock = useDeepEqualMemo(textBlockElement);

  const visible =
    !!textBlockId &&
    !!memoizedFieldTextBlock &&
    !memoizedFieldTextBlock.hidden &&
    memoizedFieldTextBlock.text === fieldText;

  /**
   * Adds the field by creating a text block element if none exists.
   * If an element was present and hidden, makes it visible again and updates its position.
   */
  const addField = useCallback(async () => {
    if (visible || !memoizedParentElement) return;

    tracking.trackAnalyticsEvent("data-layer-field-added", {
      elementId,
      fieldId,
    });

    const positions = await getDerivedTextBlockPositions(elementId, reflect);
    let idToSelect = textBlockId;

    if (textBlockId && memoizedFieldTextBlock) {
      const newPosition = findNextAvailableFieldPosition(
        positions ?? [],
        memoizedParentElement,
        memoizedFieldTextBlock.height
      );
      if (isElementCentered(memoizedParentElement)) {
        newPosition.x -= memoizedFieldTextBlock.width / 2;
        newPosition.y -= memoizedFieldTextBlock.height / 2;
      }

      await undoRedoStack?.patchElement(textBlockId, (element) => {
        element.hidden = false;
        element.x = newPosition.x;
        element.y = newPosition.y;
      });
    } else {
      const { height } = getFieldSize(
        fieldValue,
        memoizedParentElement.width * memoizedParentElement.scaleX,
        submenuData.textBlock?.fontSize
      );
      const newPosition = findNextAvailableFieldPosition(positions ?? [], memoizedParentElement, height);
      idToSelect = await addFieldToElement(elementId, memoizedParentElement, integrationId, itemId, columnId, {
        fieldValue,
        position: newPosition,
        textProperties: submenuData?.textBlock,
      });
    }

    if (idToSelect) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - For some reason the compiler thinks idToSelect can be undefined.
      canvasUtils?.setSelectedElementIds((previous: string[]) => [...previous, idToSelect]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementId, fieldId, fieldText, memoizedParentElement, visible, canvasUtils]);

  /**
   * Removes the field by making the text block hidden.
   * The element mapping is not removed.
   */
  const removeField = useCallback(async () => {
    if (!visible) return;

    tracking.trackAnalyticsEvent("data-layer-field-removed", {
      elementId,
      fieldId,
    });

    await undoRedoStack?.patchElement(textBlockId, (element) => {
      element.hidden = true;
    });

    canvasUtils?.setSelectedElementIds((previous: string[]) => previous.filter((id) => id !== textBlockId));
  }, [canvasUtils, textBlockId, undoRedoStack, visible]);

  const toggleField = useCallback(() => {
    if (visible) {
      removeField();
    } else {
      addField();
    }
  }, [visible, addField, removeField]);

  return { visible, toggleField };
};
